import Page from "@/components/Common/Page";
import Pagination from "@/components/Common/Pagination";
import {
  FormSkeleton,
  TableSkeleton,
} from "@/components/Common/SkeletonLoading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MonthPicker } from "@/components/ui/month-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import type { PluginUI } from "@/plugin-ui-contract";
import { format } from "date-fns";
import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useId,
} from "react";
import { useTranslation } from "react-i18next";

const SelectField: PluginUI["SelectField"] = ({
  children,
  value,
  onChange,
  required,
  disabled,
  name,
  id,
  className,
  "aria-label": ariaLabel,
}) => {
  const generatedId = useId();
  const options = Children.toArray(children).filter(
    isValidElement,
  ) as ReactElement<{
    value?: string;
    children?: ReactNode;
    disabled?: boolean;
  }>[];
  const placeholder = options.find((option) => option.props.value === "")?.props
    .children;
  const emptyValue = "__care_plugin_empty__";
  return (
    <Select
      name={name || generatedId}
      required={required}
      disabled={disabled}
      value={String(value || "")}
      onValueChange={(next) =>
        onChange?.({
          target: { value: next === emptyValue ? "" : next },
          currentTarget: { value: next === emptyValue ? "" : next },
        } as React.ChangeEvent<HTMLSelectElement>)
      }
    >
      <SelectTrigger id={id} aria-label={ariaLabel} className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options
          .filter((option) => !required || option.props.value !== "")
          .map((option) => (
            <SelectItem
              key={option.props.value || emptyValue}
              value={option.props.value || emptyValue}
              disabled={option.props.disabled}
            >
              {option.props.children}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
};

function Loading({ detail }: { detail?: boolean }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-label={t("loading")}
      className="my-4"
      aria-busy="true"
    >
      {detail ? (
        <Card className="p-6">
          <FormSkeleton rows={6} />
        </Card>
      ) : (
        <TableSkeleton count={5} />
      )}
    </div>
  );
}

export const pluginUI: PluginUI = {
  version: 1,
  Page: ({ title, subtitle, action, children }) => (
    <Page title={title} options={action} className="space-y-4">
      <div className="space-y-4 px-3 md:px-0">
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        {children}
      </div>
    </Page>
  ),
  Button: ({ variant = "outline", ...props }) => (
    <Button variant={variant} {...props} />
  ),
  Checkbox: ({ onCheckedChange, ...props }) => (
    <Checkbox
      {...props}
      onCheckedChange={(checked) => onCheckedChange(checked === true)}
    />
  ),
  Input,
  MonthPicker,
  DatePicker: ({ value, onChange, disabled }) => (
    <DatePicker
      date={value ? new Date(`${value}T00:00:00`) : undefined}
      onChange={(date) => onChange(date ? format(date, "yyyy-MM-dd") : "")}
      disablePicker={disabled}
    />
  ),
  Textarea,
  Field: ({ className, ...props }) => (
    <Label className={`grid gap-2 text-sm ${className || ""}`} {...props} />
  ),
  SelectField,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Badge: ({ status, children }) => (
    <Badge
      variant={
        status === "active" || status === "approved"
          ? "green"
          : status === "pending"
            ? "yellow"
            : status === "onboarding" || status === "upcoming"
              ? "blue"
              : status === "rejected"
                ? "destructive"
                : "secondary"
      }
    >
      {children}
    </Badge>
  ),
  BottomSheet: ({ title, close, children }) => (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <SheetContent
        side="bottom"
        className="rounded-t-2xl max-h-[85dvh] overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]"
        aria-describedby={undefined}
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  ),
  Modal: ({ title, busy, close, children }) => (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) close();
      }}
    >
      <DialogContent
        className="sm:max-w-2xl max-h-[90dvh] overflow-y-auto people-dialog"
        aria-describedby={undefined}
        onEscapeKeyDown={(event) => {
          if (busy) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  ),
  Notice: ({ className, children, role, ...props }) => (
    <Alert
      variant={role === "alert" ? "destructive" : "default"}
      className={className}
      role={role || "note"}
      {...props}
    >
      <AlertDescription className="flex flex-wrap items-center gap-2">
        {children}
      </AlertDescription>
    </Alert>
  ),
  Empty: EmptyState,
  Loading,
  Pagination: ({ count, offset, change }) => (
    <Pagination
      data={{ totalCount: count }}
      cPage={Math.floor(offset / 25) + 1}
      defaultPerPage={25}
      onChange={(page) => change((page - 1) * 25)}
    />
  ),
};
